import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';

const WALLET_ORDER = ['shop_pay', 'apple_pay', 'google_pay'];
const ALLOWED_WALLETS = WALLET_ORDER;
const SOURCE_ATTRS = [
  'access-token',
  'buyer-country',
  'buyer-currency',
  'buyer-locale',
  'shop-id',
  'enabled-flags',
];
const JSON_HEADERS = { Accept: 'application/json' };
const WALLET_HYDRATION_MS = 800;

/** @type {Promise<Array<Record<string, unknown>> | null> | null} */
let walletConfigsPromise = null;
/** @type {string | null} */
let walletConfigsVariantId = null;
/** @type {Promise<Record<string, unknown>> | null} */
let expressCheckoutCartSyncPromise = null;
/** @type {string | null} */
let preparedCartKey = null;

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function getFormCartKey(form) {
  const formData = new FormData(form);
  const sellingPlan = formData.get('selling_plan') || '';
  return `${formData.get('id')}:${formData.get('quantity') || 1}:${sellingPlan}`;
}

/**
 * @returns {Promise<Record<string, unknown>>}
 */
async function fetchCartJson() {
  const response = await fetch('/cart.js', { headers: JSON_HEADERS });
  return response.json();
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter((id) => Boolean(id));
}

/**
 * @param {Record<string, unknown>} cart
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function cartMatchesForm(cart, form) {
  const formData = new FormData(form);
  const variantId = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity') || 1);

  if (!variantId || !Array.isArray(cart.items) || cart.items.length !== 1) return false;

  const item = cart.items[0];
  return item.variant_id === variantId && item.quantity === quantity;
}

/**
 * @param {Record<string, unknown>} originalCart
 * @returns {Promise<void>}
 */
async function restoreCart(originalCart) {
  await fetch('/cart/clear.js', { method: 'POST' });

  for (const item of originalCart.items || []) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { ...JSON_HEADERS, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.variant_id, quantity: item.quantity }),
    });
  }
}

/**
 * @param {Record<string, unknown>} cart
 * @param {HTMLFormElement} form
 * @param {Record<string, string>} [sections]
 */
function publishCartReady(cart, form, sections = {}) {
  const productForm = form.closest('product-form-component');
  const productId = productForm instanceof HTMLElement ? productForm.dataset.productId : undefined;
  const itemCount = Number(cart.item_count) || 0;

  document.dispatchEvent(
    new CartAddEvent(cart, form.id || 'product-wallet-buttons', {
      source: 'product-wallet-buttons',
      itemCount,
      productId,
      variantId: String(new FormData(form).get('id') ?? ''),
      sections,
    })
  );

  document.dispatchEvent(
    new CartUpdateEvent(cart, form.id || 'product-wallet-buttons', {
      source: 'product-wallet-buttons',
      itemCount,
      productId,
      variantId: String(new FormData(form).get('id') ?? ''),
      sections,
    })
  );
}

/**
 * Opens the cart drawer so Apple Pay / wallets use a live cart session.
 */
function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
}

/**
 * Adds the current PDP line to cart and refreshes cart drawer sections.
 *
 * @param {HTMLFormElement} form
 * @returns {Promise<Record<string, unknown>>}
 */
async function syncCartForExpressCheckout(form) {
  const formData = new FormData(form);
  const variantId = Number(formData.get('id'));

  if (!variantId) {
    const cart = await fetchCartJson();
    publishCartReady(cart, form);
    openCartDrawer();
    return cart;
  }

  if (expressCheckoutCartSyncPromise) {
    const pending = await expressCheckoutCartSyncPromise;
    return pending.cart;
  }

  const sectionIds = getCartSectionIds();
  for (const sectionId of sectionIds) {
    formData.append('sections', sectionId);
  }

  expressCheckoutCartSyncPromise = (async () => {
    const cart = await fetchCartJson();

    if (cartMatchesForm(cart, form)) {
      preparedCartKey = getFormCartKey(form);
      return { cart, sections: /** @type {Record<string, string>} */ ({}) };
    }

    await fetch('/cart/clear.js', { method: 'POST' });
    const addResponse = await fetch('/cart/add.js', { method: 'POST', body: formData });
    const addResult = await addResponse.json();

    if (addResult.status) {
      preparedCartKey = null;
      throw new Error(addResult.message || 'Unable to add product to cart for express checkout.');
    }

    const updatedCart = await fetchCartJson();
    preparedCartKey = getFormCartKey(form);
    return { cart: updatedCart, sections: addResult.sections || {} };
  })();

  try {
    const { cart, sections } = await expressCheckoutCartSyncPromise;
    publishCartReady(cart, form, sections);
    openCartDrawer();
    return cart;
  } finally {
    expressCheckoutCartSyncPromise = null;
  }
}

/**
 * Bootstrap Shop / Apple / Google configs from cart markup without leaving the product in cart.
 *
 * @param {FormData} formData
 * @returns {Promise<Array<Record<string, unknown>> | null>}
 */
async function fetchWalletConfigs(formData) {
  const variantId = String(formData.get('id') ?? '');

  if (walletConfigsPromise && walletConfigsVariantId === variantId) {
    return walletConfigsPromise;
  }

  walletConfigsVariantId = variantId;
  walletConfigsPromise = (async () => {
    try {
      const originalCart = await fetchCartJson();

      await fetch('/cart/clear.js', { method: 'POST' });
      await fetch('/cart/add.js', { method: 'POST', body: formData });

      const html = await (await fetch('/cart')).text();
      const match = html.match(/wallet-configs="([^"]+)"/);

      await restoreCart(originalCart);

      if (!match) return null;

      const configs = JSON.parse(match[1].replace(/&quot;/g, '"'));
      return configs
        .filter((config) => ALLOWED_WALLETS.includes(config.name))
        .sort((a, b) => WALLET_ORDER.indexOf(a.name) - WALLET_ORDER.indexOf(b.name));
    } catch {
      walletConfigsPromise = null;
      walletConfigsVariantId = null;
      return null;
    }
  })();

  return walletConfigsPromise;
}

/**
 * Reads the live cart checkout host rendered by Shopify on /cart.
 *
 * @returns {Promise<{ attrs: Record<string, string>, configs: Array<Record<string, unknown>> } | null>}
 */
async function fetchCartWalletData() {
  const html = await (await fetch('/cart')).text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const host = doc.querySelector('shopify-accelerated-checkout-cart');

  if (!host) return null;

  /** @type {Record<string, string>} */
  const attrs = {};

  for (const name of host.getAttributeNames()) {
    const value = host.getAttribute(name);
    if (value) attrs[name] = value;
  }

  if (!attrs['access-token'] || !attrs['wallet-configs']) return null;

  try {
    const configs = JSON.parse(attrs['wallet-configs']);
    return {
      attrs,
      configs: configs
        .filter((config) => ALLOWED_WALLETS.includes(config.name))
        .sort((a, b) => WALLET_ORDER.indexOf(a.name) - WALLET_ORDER.indexOf(b.name)),
    };
  } catch {
    return null;
  }
}

/**
 * @param {Element} source
 * @returns {Array<Record<string, unknown>>}
 */
function getFallbackWalletConfigs(source) {
  /** @type {Array<Record<string, unknown>>} */
  const configs = [];

  for (const attr of ['recommended', 'fallback']) {
    const value = source.getAttribute(attr);
    if (!value) continue;

    try {
      const config = JSON.parse(value);
      if (config?.name && ALLOWED_WALLETS.includes(config.name) && !configs.some((c) => c.name === config.name)) {
        configs.push(config);
      }
    } catch {
      // Ignore malformed wallet config attributes.
    }
  }

  for (const name of WALLET_ORDER) {
    if (!configs.some((config) => config.name === name)) {
      configs.push({ name });
    }
  }

  return configs.sort((a, b) => WALLET_ORDER.indexOf(a.name) - WALLET_ORDER.indexOf(b.name));
}

/**
 * @param {Element} source
 * @returns {Record<string, string> | null}
 */
function getProductSourceAttrs(source) {
  /** @type {Record<string, string>} */
  const attrs = {};

  for (const name of SOURCE_ATTRS) {
    const value = source.getAttribute(name);
    if (value) attrs[name] = value;
  }

  return Object.keys(attrs).length ? attrs : null;
}

/**
 * @returns {Promise<void>}
 */
async function waitForWalletHydration() {
  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
  await new Promise((resolve) => window.setTimeout(resolve, WALLET_HYDRATION_MS));
}

/**
 * @param {HTMLElement} walletsContainer
 * @param {string | null} walletName
 * @param {number} walletIndex
 */
function clickWallet(walletsContainer, walletName, walletIndex) {
  const slots = walletsContainer.querySelectorAll('.accelerated-checkout-block__wallet');

  let host = null;
  if (walletName) {
    const namedSlot = walletsContainer.querySelector(`[data-wallet-name="${walletName}"]`);
    host = namedSlot?.querySelector('shopify-accelerated-checkout-cart') ?? null;
  }

  if (!host) {
    host = slots[walletIndex]?.querySelector('shopify-accelerated-checkout-cart') ?? null;
  }

  // Prefer native cart drawer wallet host when available — same session as checkout.
  const drawerHost = document.querySelector(
    'cart-drawer-component shopify-accelerated-checkout-cart, .cart-drawer shopify-accelerated-checkout-cart'
  );

  if (drawerHost instanceof HTMLElement) {
    drawerHost.click();
    return;
  }

  host?.click();
}

/**
 * @param {PointerEvent} event
 * @param {HTMLElement} walletsContainer
 * @returns {{ index: number, name: string | null }}
 */
function getWalletTarget(event, walletsContainer) {
  const slot = event.target instanceof Element ? event.target.closest('.accelerated-checkout-block__wallet') : null;
  if (!slot) return { index: 0, name: null };

  const slots = walletsContainer.querySelectorAll('.accelerated-checkout-block__wallet');
  return {
    index: Math.max(0, Array.from(slots).indexOf(slot)),
    name: slot instanceof HTMLElement ? slot.dataset.walletName || null : null,
  };
}

/**
 * @param {Record<string, string>} hostAttrs
 * @param {Record<string, unknown>} config
 * @returns {HTMLElement}
 */
function createCartWalletElement(hostAttrs, config) {
  const cartElement = document.createElement('shopify-accelerated-checkout-cart');

  for (const [name, value] of Object.entries(hostAttrs)) {
    if (name === 'wallet-configs') continue;
    cartElement.setAttribute(name, value);
  }

  cartElement.setAttribute('wallet-configs', JSON.stringify([config]));
  return cartElement;
}

/**
 * Remount wallet hosts from the live /cart session after the product is in cart.
 *
 * @param {HTMLElement} block
 * @returns {Promise<boolean>}
 */
async function remountWalletsFromCart(block) {
  const walletsContainer = block.querySelector('.accelerated-checkout-block__wallets');
  if (!(walletsContainer instanceof HTMLElement)) return false;

  const walletData = await fetchCartWalletData();
  if (!walletData?.configs.length) return false;

  walletsContainer.innerHTML = '';

  for (const config of walletData.configs) {
    const walletSlot = document.createElement('div');
    walletSlot.className =
      'accelerated-checkout-block__wallet additional-checkout-buttons additional-checkout-buttons--vertical';
    walletSlot.dataset.walletName = String(config.name);
    walletSlot.appendChild(createCartWalletElement(walletData.attrs, config));
    walletsContainer.appendChild(walletSlot);
  }

  return true;
}

/**
 * Pay now: add product → open cart drawer → remount cart wallets → open tapped wallet.
 *
 * @param {HTMLElement} block
 * @param {HTMLFormElement} form
 */
function bindWalletPayNow(block, form) {
  if (block.dataset.walletPayNowBound === 'true') return;

  block.dataset.walletPayNowBound = 'true';

  block.addEventListener(
    'pointerdown',
    async (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('.accelerated-checkout-block__wallets shopify-accelerated-checkout-cart')) return;

      if (block.dataset.walletBypass === 'true') {
        block.dataset.walletBypass = 'false';
        return;
      }

      const walletsContainer = block.querySelector('.accelerated-checkout-block__wallets');
      if (!(walletsContainer instanceof HTMLElement)) return;

      const cartKey = getFormCartKey(form);
      const alreadyPrepared =
        preparedCartKey === cartKey && walletsContainer.dataset.cartReady === 'true';

      if (alreadyPrepared) {
        openCartDrawer();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const { index, name } = getWalletTarget(event, walletsContainer);
      walletsContainer.setAttribute('data-wallet-loading', 'true');
      walletsContainer.dataset.cartReady = 'false';

      try {
        await syncCartForExpressCheckout(form);
        await waitForWalletHydration();

        const remounted = await remountWalletsFromCart(block);
        if (!remounted) {
          preparedCartKey = null;
          return;
        }

        walletsContainer.dataset.cartReady = 'true';
        await waitForWalletHydration();

        block.dataset.walletBypass = 'true';
        clickWallet(walletsContainer, name, index);
      } catch {
        preparedCartKey = null;
        walletsContainer.dataset.cartReady = 'false';
      } finally {
        walletsContainer.removeAttribute('data-wallet-loading');
      }
    },
    { capture: true }
  );
}

/**
 * Show compact wallet buttons on PDP without leaving the product in the shopper cart.
 *
 * @param {HTMLElement} block
 * @returns {Promise<void>}
 */
async function renderPlaceholderWallets(block) {
  const walletsContainer = block.querySelector('.accelerated-checkout-block__wallets');
  const source = block.querySelector('.accelerated-checkout-block__source shopify-accelerated-checkout');

  if (!walletsContainer || !source || block.hasAttribute('hidden')) return;

  const form = block.closest('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return;

  if (!source.getAttribute('access-token')) return;

  const attrs = getProductSourceAttrs(source);
  if (!attrs) return;

  let configs = await fetchWalletConfigs(new FormData(form));
  if (!configs?.length) {
    configs = getFallbackWalletConfigs(source);
  }

  if (!configs.length) return;

  preparedCartKey = null;
  walletsContainer.dataset.cartReady = 'false';
  walletsContainer.innerHTML = '';

  for (const config of configs) {
    const walletSlot = document.createElement('div');
    walletSlot.className =
      'accelerated-checkout-block__wallet additional-checkout-buttons additional-checkout-buttons--vertical';
    walletSlot.dataset.walletName = String(config.name);
    walletSlot.appendChild(createCartWalletElement(attrs, config));
    walletsContainer.appendChild(walletSlot);
  }

  bindWalletPayNow(block, form);
}

/**
 * @param {HTMLElement} block
 */
function initBlock(block) {
  /** @type {MutationObserver | undefined} */
  let observer;
  /** @type {number[]} */
  const retryTimeouts = [];

  const tryRender = () => {
    const source = block.querySelector('.accelerated-checkout-block__source shopify-accelerated-checkout');
    if (!source?.getAttribute('access-token')) return;

    void renderPlaceholderWallets(block);
    observer?.disconnect();
    for (const timeoutId of retryTimeouts) {
      clearTimeout(timeoutId);
    }
    retryTimeouts.length = 0;
  };

  observer = new MutationObserver(tryRender);
  observer.observe(block, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['access-token', 'variant-params', 'hidden'],
  });

  tryRender();

  for (const delay of [500, 1500, 3000]) {
    retryTimeouts.push(window.setTimeout(tryRender, delay));
  }
}

function init() {
  document.querySelectorAll('.accelerated-checkout-block').forEach((block) => {
    if (block instanceof HTMLElement) initBlock(block);
  });

  document.addEventListener(ThemeEvents.variantUpdate, () => {
    walletConfigsPromise = null;
    walletConfigsVariantId = null;
    preparedCartKey = null;

    document.querySelectorAll('.accelerated-checkout-block').forEach((block) => {
      if (block instanceof HTMLElement && !block.hasAttribute('hidden')) {
        void renderPlaceholderWallets(block);
      }
    });
  });

  document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => {
    preparedCartKey = null;

    document.querySelectorAll('.accelerated-checkout-block').forEach((block) => {
      if (block instanceof HTMLElement && !block.hasAttribute('hidden')) {
        const walletsContainer = block.querySelector('.accelerated-checkout-block__wallets');
        if (walletsContainer instanceof HTMLElement) {
          walletsContainer.dataset.cartReady = 'false';
        }
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
